import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Users, 
  RefreshCw,
  AlertTriangle,
  Info,
  Download,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { ReincidenciaSkeleton } from '../components/ui/skeleton-loaders';
import { InfoTooltip, SectionHeader, DefinitionsPanel, EmptyState } from '../components/ui/info-components';
import { secureExportDownload } from '../utils/secureExport';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Cache simple para evitar re-fetch innecesarios
const cache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutos

export default function ReincidenciaView({ token, selectedPartner = 'global' }) {
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [period, setPeriod] = useState('ytd');
  const [umbral, setUmbral] = useState(2);
  const [data, setData] = useState(null);
  const [showFullList, setShowFullList] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [listPage, setListPage] = useState(1);
  const [paginatedList, setPaginatedList] = useState([]);
  
  // Ref para evitar doble fetch
  const fetchingRef = useRef(false);

  // Generar cache key
  const getCacheKey = useCallback((includeList = false, page = 1) => {
    const instancia = selectedPartner !== 'global' ? selectedPartner : '';
    return `reincidencia-${period}-${umbral}-${instancia}-${includeList}-${page}`;
  }, [period, umbral, selectedPartner]);

  // Fetch inicial (sin listado completo - RÁPIDO)
  const fetchData = useCallback(async (forceRefresh = false) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    
    const cacheKey = getCacheKey(false);
    const cached = cache.get(cacheKey);
    
    // Usar cache si existe y no está expirado
    if (!forceRefresh && cached && Date.now() - cached.timestamp < CACHE_TTL) {
      setData(cached.data);
      setLoading(false);
      fetchingRef.current = false;
      return;
    }
    
    setLoading(true);
    const instancia = selectedPartner !== 'global' ? selectedPartner : '';
    const params = `period=${period}&umbral=${umbral}&include_listado=false${instancia ? `&instancia=${instancia}` : ''}`;
    
    try {
      const res = await fetch(`${API_URL}/api/atencion/derivaciones/reincidencia?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const responseData = await res.json();
        setData(responseData);
        cache.set(cacheKey, { data: responseData, timestamp: Date.now() });
      }
    } catch (error) {
      console.error('Error fetching reincidencia:', error);
    }
    setLoading(false);
    fetchingRef.current = false;
  }, [token, period, umbral, selectedPartner, getCacheKey]);

  // Fetch paginado del listado (lazy load)
  const fetchPaginatedList = useCallback(async (page = 1) => {
    const cacheKey = getCacheKey(true, page);
    const cached = cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      setPaginatedList(prev => page === 1 ? cached.data : [...prev, ...cached.data]);
      setListPage(page);
      return;
    }
    
    setLoadingMore(true);
    const instancia = selectedPartner !== 'global' ? selectedPartner : '';
    const params = `period=${period}&umbral=${umbral}&include_listado=true&page=${page}&per_page=50${instancia ? `&instancia=${instancia}` : ''}`;
    
    try {
      const res = await fetch(`${API_URL}/api/atencion/derivaciones/reincidencia?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const responseData = await res.json();
        const newList = responseData.listado_alumnos || [];
        cache.set(cacheKey, { data: newList, timestamp: Date.now() });
        setPaginatedList(prev => page === 1 ? newList : [...prev, ...newList]);
        setListPage(page);
        // Actualizar metadata de paginación
        if (responseData.pagination) {
          setData(prev => ({ ...prev, pagination: responseData.pagination }));
        }
      }
    } catch (error) {
      console.error('Error fetching paginated list:', error);
    }
    setLoadingMore(false);
  }, [token, period, umbral, selectedPartner, getCacheKey]);

  // Handler para "Ver Todos"
  const handleToggleFullList = async () => {
    if (!showFullList) {
      // Primera vez que se expande: cargar primera página
      if (paginatedList.length === 0) {
        await fetchPaginatedList(1);
      }
    }
    setShowFullList(!showFullList);
  };

  // Handler para cargar más
  const handleLoadMore = () => {
    const pagination = data?.pagination;
    if (pagination?.has_more) {
      fetchPaginatedList(listPage + 1);
    }
  };

  // Handler para exportar CSV
  const handleExportCSV = async () => {
    setExporting(true);
    const instancia = selectedPartner !== 'global' ? selectedPartner : '';
    const params = `period=${period}&umbral=${umbral}${instancia ? `&instancia=${instancia}` : ''}`;
    
    try {
      const url = `${API_URL}/api/atencion/derivaciones/reincidencia/export?${params}`;
      await secureExportDownload({ url, filename: `reincidencia_${period}_${selectedPartner}_umbral${umbral}.xlsx` });
    } catch (error) {
      if (error.message !== 'cancelado') {
        console.error('Error exporting CSV:', error);
      }
    }
    setExporting(false);
  };

  // Effect para fetch inicial
  useEffect(() => {
    if (token) {
      setShowFullList(false);
      setPaginatedList([]);
      setListPage(1);
      fetchData();
    }
  }, [token, period, umbral, selectedPartner]);

  // Mostrar skeleton mientras carga
  if (loading) {
    return <ReincidenciaSkeleton />;
  }

  const resumen = data?.resumen || {};
  const topAlumnos = data?.top_alumnos_reincidentes || data?.top_reincidentes || [];
  const totalListado = data?.total_listado || 0;
  const pagination = data?.pagination || {};
  const definiciones = data?.definiciones || {};

  // Lista a mostrar: top 20 o paginada
  const displayList = showFullList ? paginatedList : topAlumnos.slice(0, 20);

  return (
    <div className="space-y-6">
      {/* Header con descripción */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <SectionHeader 
          title="Reincidencia de Alumnos"
          description="Identifica alumnos que abren múltiples tickets — casos problemáticos o productos con fricción recurrente."
          icon={Users}
        />
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-sm text-slate-600 font-medium">Umbral:</label>
            <select
              value={umbral}
              onChange={(e) => setUmbral(Number(e.target.value))}
              className="px-3 py-1.5 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="umbral-select"
            >
              <option value={2}>≥2 tickets</option>
              <option value={3}>≥3 tickets</option>
              <option value={5}>≥5 tickets</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-slate-600 font-medium">Período:</label>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="px-3 py-1.5 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="period-select"
            >
              <option value="mtd">Mes Actual</option>
              <option value="ytd">Año 2026</option>
              <option value="last30">Últimos 30 días</option>
              <option value="last90">Últimos 90 días</option>
            </select>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
            disabled={exporting || totalListado === 0}
            className="flex items-center gap-1.5"
            data-testid="export-csv-btn"
            title={totalListado === 0 ? 'No hay datos para exportar con los filtros actuales' : undefined}
          >
            <Download className="w-4 h-4" />
            {exporting ? 'Exportando...' : 'Descargar Excel'}
          </Button>
          <button
            onClick={() => fetchData(true)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Actualizar datos"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      </div>

      {/* KPIs con tooltips */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-100">
          <CardContent className="pt-4">
            <div className="flex items-center gap-1">
              <p className="text-sm text-purple-600 font-medium">Alumnos Únicos</p>
              <InfoTooltip text="Total de alumnos distintos que abrieron al menos 1 ticket en el período" />
            </div>
            <p className="text-3xl font-bold text-purple-700" data-testid="kpi-alumnos-unicos">
              {resumen.total_alumnos_unicos?.toLocaleString() || 0}
            </p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-50 to-white border-red-100">
          <CardContent className="pt-4">
            <div className="flex items-center gap-1">
              <p className="text-sm text-red-600 font-medium">Alumnos Reincidentes</p>
              <InfoTooltip text={`Alumnos con ≥${umbral} tickets en el período seleccionado`} />
            </div>
            <p className="text-3xl font-bold text-red-700" data-testid="kpi-reincidentes">
              {resumen.alumnos_reincidentes?.toLocaleString() || 0}
            </p>
            <p className="text-xs text-red-500 mt-1">≥{umbral} tickets en el período</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-100">
          <CardContent className="pt-4">
            <div className="flex items-center gap-1">
              <p className="text-sm text-amber-600 font-medium">% Reincidencia</p>
              <InfoTooltip text="Fórmula: Alumnos reincidentes ÷ Total alumnos × 100" />
            </div>
            <p className="text-3xl font-bold text-amber-700" data-testid="kpi-pct-reincidencia">
              {resumen.pct_reincidencia || 0}%
            </p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-100">
          <CardContent className="pt-4">
            <div className="flex items-center gap-1">
              <p className="text-sm text-blue-600 font-medium">Total Tickets</p>
              <InfoTooltip text="Cantidad total de tickets creados en el período" />
            </div>
            <p className="text-3xl font-bold text-blue-700" data-testid="kpi-total-tickets">
              {resumen.total_tickets?.toLocaleString() || 0}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabla de alumnos reincidentes */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Alumnos Reincidentes ({totalListado.toLocaleString()})
              <InfoTooltip text="Lista ordenada por cantidad de tickets (mayor a menor)" />
            </CardTitle>
            {totalListado > 20 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleToggleFullList}
                className="flex items-center gap-1 text-sm"
                data-testid="toggle-full-list-btn"
              >
                {showFullList ? (
                  <>
                    <ChevronUp className="w-4 h-4" />
                    Ver Top 20
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    Ver Todos ({totalListado.toLocaleString()})
                  </>
                )}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {displayList.length === 0 ? (
            <EmptyState 
              title="No hay alumnos reincidentes"
              message={`No se encontraron alumnos con ≥${umbral} tickets en este período.`}
              icon={Users}
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="alumnos-table">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="text-left py-2 px-3 font-medium text-slate-600">Email</th>
                      <th className="text-right py-2 px-3 font-medium text-slate-600">
                        Total Tickets
                        <InfoTooltip text="Cantidad de tickets abiertos por este alumno en el período" />
                      </th>
                      <th className="text-right py-2 px-3 font-medium text-slate-600">
                        Tickets/Mes
                        <InfoTooltip text="Total tickets ÷ Meses con al menos 1 ticket" />
                      </th>
                      <th className="text-right py-2 px-3 font-medium text-slate-600">
                        Reaperturas
                        <InfoTooltip text="Tickets marcados como 'Reabierto' en HubSpot" />
                      </th>
                      <th className="text-left py-2 px-3 font-medium text-slate-600">Instancia</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayList.map((alumno, idx) => (
                      <tr key={idx} className="border-b hover:bg-slate-50 transition-colors">
                        <td className="py-2 px-3 font-mono text-xs">
                          {showFullList 
                            ? alumno.email 
                            : (alumno.email?.length > 25 
                                ? alumno.email.substring(0, 3) + '***' + alumno.email.substring(alumno.email.indexOf('@')) 
                                : alumno.email)
                          }
                        </td>
                        <td className="text-right py-2 px-3">
                          <Badge variant="destructive" className="font-mono">
                            {alumno.total_tickets}
                          </Badge>
                        </td>
                        <td className="text-right py-2 px-3 text-slate-600">
                          {alumno.tickets_por_mes}
                        </td>
                        <td className="text-right py-2 px-3">
                          {alumno.reaperturas > 0 ? (
                            <Badge variant="outline" className="text-amber-600 border-amber-300">
                              {alumno.reaperturas}
                            </Badge>
                          ) : (
                            <span className="text-slate-400">0</span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-slate-600 text-xs">{alumno.instancia || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Paginación / Cargar más */}
              {showFullList && pagination.has_more && (
                <div className="mt-4 flex justify-center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="flex items-center gap-2"
                  >
                    {loadingMore ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Cargando...
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4" />
                        Cargar más ({paginatedList.length} de {totalListado})
                      </>
                    )}
                  </Button>
                </div>
              )}
              
              {/* Footer con info de paginación */}
              {!showFullList && totalListado > 20 && (
                <div className="mt-3 text-center text-sm text-slate-500 border-t pt-3">
                  Mostrando 20 de {totalListado.toLocaleString()} alumnos. 
                  <button 
                    onClick={handleToggleFullList}
                    className="ml-1 text-blue-600 hover:underline"
                  >
                    Ver todos
                  </button>
                  {' '}o{' '}
                  <button 
                    onClick={handleExportCSV}
                    className="text-blue-600 hover:underline"
                  >
                    descargar CSV
                  </button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Panel de definiciones al pie */}
      <DefinitionsPanel 
        definitions={{
          'Reincidente': `Alumno con ≥${umbral} tickets en el período`,
          '% Reincidencia': 'Alumnos reincidentes ÷ Total alumnos × 100',
          'Tickets/Mes': 'Total tickets ÷ Meses activos (con al menos 1 ticket)',
          'Reapertura': 'Ticket que fue cerrado y luego reabierto'
        }}
      />
    </div>
  );
}
